/**
 * ParametresCantinePage — réglages de la cantine, réservés maire/admin.
 * - Délai de prévenance (cutoff 24/48/72h).
 * - Règles de facturation des absences (derrière un interrupteur). Tant que la
 *   facturation est désactivée, les règles sont grisées (configuration seulement,
 *   le calcul effectif viendra avec le lot Facturation).
 */

import { useEffect, useState } from 'react';
import { Settings, Save, AlertCircle, Info, Upload, Image as ImageIcon } from 'lucide-react';
import { api, ApiException } from '@/lib/api';
import { compressImageIfNeeded } from '@/lib/image-compress';
import { Card, CardBody, Button, Field, Input, Alert, Badge } from '@/components/ui';
import { ScolaireLayout } from '@/components/scolaire/ScolaireLayout';
import type { ScolaireParametres, AnnulationHorsDelai, RepasNonReserve } from '@roronoa-collectivites/shared-types';

const CUTOFFS: { value: 24 | 48 | 72; label: string }[] = [
  { value: 24, label: '24 h' }, { value: 48, label: '48 h' }, { value: 72, label: '72 h' },
];
const ANNULATION: { value: AnnulationHorsDelai; label: string; desc: string }[] = [
  { value: 'non_facture', label: 'Non facturé', desc: 'Une annulation tardive n\'est jamais facturée.' },
  { value: 'facture', label: 'Facturé', desc: 'Le repas est dû dès que l\'annulation est hors délai.' },
  { value: 'facture_sauf_justificatif', label: 'Facturé sauf justificatif', desc: 'Facturé, sauf si la famille fournit un justificatif (maladie…).' },
];
const REPAS_NON_RESERVE: { value: RepasNonReserve; label: string }[] = [
  { value: 'non_facture', label: 'Non facturé' },
  { value: 'facture', label: 'Facturé (tarif normal)' },
  { value: 'facture_majore', label: 'Facturé (tarif majoré)' },
];

export function ParametresCantinePage() {
  const [params, setParams] = useState<ScolaireParametres | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [autoRun, setAutoRun] = useState<{ busy: boolean; msg: string | null }>({ busy: false, msg: null });

  // Config en-tête de facture
  const [entete, setEntete] = useState<{ mode: 'auto' | 'image' | 'pdf'; aUneImage: boolean; mentionLegale: string | null; iban: string | null } | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await api.scolaire.getParametres();
        if (!cancelled) setParams(p);
        const e = await api.scolaire.facturation.getEntete();
        if (!cancelled) setEntete(e);
      }
      catch (e) { if (!cancelled) setError(e instanceof ApiException ? e.message : 'Erreur'); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  async function uploadEntete(file: File) {
    setUploading(true); setError(null);
    try {
      const compressed = await compressImageIfNeeded(file);
      await api.scolaire.facturation.uploadEnteteImage(compressed);
      const e = await api.scolaire.facturation.getEntete();
      setEntete(e); setOk(true);
    } catch (err) { setError(err instanceof ApiException ? err.message : 'Échec du téléversement'); }
    finally { setUploading(false); }
  }

  async function changerModeEntete(mode: 'auto' | 'image' | 'pdf') {
    if (!entete) return;
    try {
      await api.scolaire.facturation.setEntete({ mode, mentionLegale: entete.mentionLegale, iban: entete.iban });
      setEntete({ ...entete, mode });
    } catch (err) { setError(err instanceof ApiException ? err.message : 'Erreur'); }
  }

  async function sauverEnteteTexte() {
    if (!entete) return;
    try {
      await api.scolaire.facturation.setEntete({ mode: entete.mode, mentionLegale: entete.mentionLegale, iban: entete.iban });
      setOk(true);
    } catch (err) { setError(err instanceof ApiException ? err.message : 'Erreur'); }
  }

  function patch<K extends keyof ScolaireParametres>(k: K, v: ScolaireParametres[K]) {
    setParams((p) => (p ? { ...p, [k]: v } : p));
    setOk(false);
    setDirty(true);
  }

  async function execAuto() {
    setAutoRun({ busy: true, msg: null });
    try {
      const r = await api.scolaire.facturation.executerAuto();
      setAutoRun({ busy: false, msg: `${r.facturesEmises} facture(s) émise(s) pour ${r.famillesTraitees} famille(s) — période du ${r.periode.debut} au ${r.periode.fin}.${r.erreurs ? ` ${r.erreurs} erreur(s).` : ''}` });
    } catch (e) {
      setAutoRun({ busy: false, msg: null });
      setError(e instanceof ApiException ? e.message : 'Échec de l’émission automatique');
    }
  }

  async function enregistrer() {
    if (!params) return;
    setSaving(true); setError(null); setOk(false);
    try {
      const next = await api.scolaire.setParametres({
        cutoffHours: params.cutoffHours,
        facturationActive: params.facturationActive,
        annulationHorsDelai: params.annulationHorsDelai,
        repasNonReserve: params.repasNonReserve,
        delaiJustificatifJours: params.delaiJustificatifJours,
        delaiPaiementJours: params.delaiPaiementJours,
        mentionPiedFacture: params.mentionPiedFacture,
        facturationUniteMinutes: params.facturationUniteMinutes as 15 | 30 | 60,
        facturationArrondi: params.facturationArrondi,
        modeFacturationCantine: params.modeFacturationCantine,
        modeFacturationPeriscolaire: params.modeFacturationPeriscolaire,
        modeFacturationAlsh: params.modeFacturationAlsh,
        autoFacturationActive: params.autoFacturationActive,
        autoFacturationJour: params.autoFacturationJour,
        autoFacturationHeure: params.autoFacturationHeure,
        autoFacturationPeriode: params.autoFacturationPeriode,
        autoFacturationGroupee: params.autoFacturationGroupee,
        autoFacturationServices: params.autoFacturationServices,
        autoFacturationEnvoiEmail: params.autoFacturationEnvoiEmail,
      });
      setParams(next); setOk(true); setDirty(false);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur d\'enregistrement'); }
    finally { setSaving(false); }
  }

  return (
    <ScolaireLayout activeTab="parametres">
      <main className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
        <div className="mb-5 flex items-center gap-3">
          <span className="inline-grid h-10 w-10 place-items-center rounded-r-lg bg-cantine-tint text-cantine-fg"><Settings className="h-5 w-5" /></span>
          <div>
            <h1 className="font-display text-2xl font-semibold tracking-r-tight text-ink">Paramètres de la cantine</h1>
            <p className="mt-1 text-sm text-ink-muted">Délai de prévenance et règles de facturation. Réservé au maire et aux adjoints.</p>
          </div>
        </div>
        {error && <Alert variant="error" icon={AlertCircle} className="mb-4">{error}</Alert>}
        {ok && <Alert variant="success" className="mb-4">Paramètres enregistrés.</Alert>}

        {loading || !params ? (
          <p className="text-sm text-ink-muted">Chargement…</p>
        ) : (
          <div className="space-y-4">
            {/* Cutoff */}
            <Card>
              <CardBody>
                <h2 className="mb-1 font-display text-lg font-semibold text-ink">Délai de prévenance</h2>
                <p className="mb-3 text-sm text-ink-muted">Combien de temps à l'avance une famille peut annuler/modifier une réservation.</p>
                <div className="flex gap-2">
                  {CUTOFFS.map((c) => (
                    <button key={c.value} type="button" onClick={() => patch('cutoffHours', c.value)}
                      className={['inline-flex h-10 items-center rounded-r-md px-4 text-sm font-medium transition-colors',
                        params.cutoffHours === c.value ? 'bg-cantine text-white shadow-soft' : 'border border-border text-ink-muted hover:bg-surface/70'].join(' ')}>
                      {c.label}
                    </button>
                  ))}
                </div>
              </CardBody>
            </Card>

            {/* Facturation */}
            <Card>
              <CardBody>
                <div className="mb-1 flex items-center justify-between gap-2">
                  <h2 className="font-display text-lg font-semibold text-ink">Règles de facturation des absences</h2>
                  <label className="inline-flex cursor-pointer items-center gap-2">
                    <input type="checkbox" checked={params.facturationActive} onChange={(e) => patch('facturationActive', e.target.checked)} className="h-4 w-4 accent-[oklch(var(--c-cantine))]" />
                    <span className="text-sm text-ink">{params.facturationActive ? 'Appliquées' : 'Non appliquées'}</span>
                  </label>
                </div>
                <Alert variant="info" icon={Info} className="mb-3">
                  Les repas réservés ou pointés présents sont <strong>toujours facturés</strong> dès qu'un tarif
                  existe. Cet interrupteur ne concerne que le traitement des <strong>absences et annulations</strong> :
                  s'il est désactivé, les absences ne sont jamais facturées.
                </Alert>

                <fieldset disabled={!params.facturationActive} className={params.facturationActive ? '' : 'opacity-50'}>
                  <div className="mb-4">
                    <span className="mb-1.5 block text-sm font-medium text-ink">Annulation hors délai</span>
                    <div className="space-y-1.5">
                      {ANNULATION.map((a) => (
                        <label key={a.value} className="flex cursor-pointer items-start gap-2 rounded-r-md border border-border p-2.5 hover:bg-surface/60">
                          <input type="radio" name="annul" checked={params.annulationHorsDelai === a.value} onChange={() => patch('annulationHorsDelai', a.value)} className="mt-0.5 accent-[oklch(var(--c-cantine))]" />
                          <span>
                            <span className="block text-sm font-medium text-ink">{a.label}</span>
                            <span className="block text-xs text-ink-muted">{a.desc}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <Field label="Repas pris sans réservation (badgé au pointage)" htmlFor="rnr">
                    <select id="rnr" value={params.repasNonReserve} onChange={(e) => patch('repasNonReserve', e.target.value as RepasNonReserve)}
                      className="h-10 w-full rounded-r-md border border-border bg-surface px-3 text-sm text-ink">
                      {REPAS_NON_RESERVE.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </Field>

                  {params.annulationHorsDelai === 'facture_sauf_justificatif' && (
                    <div className="mt-3">
                      <Field label="Délai pour fournir un justificatif (jours)" htmlFor="delai">
                        <Input id="delai" type="number" min={0} max={30} value={params.delaiJustificatifJours}
                          onChange={(e) => patch('delaiJustificatifJours', Math.max(0, Math.min(30, Number(e.target.value) || 0)))} />
                      </Field>
                    </div>
                  )}
                </fieldset>
              </CardBody>
            </Card>

            {/* Mode de facturation par service (forfait / au temps) */}
            <Card>
              <CardBody>
                <h2 className="mb-1 font-display text-lg font-semibold text-ink">Mode de facturation par service</h2>
                <Alert variant="info" icon={Info} className="mb-3">
                  <strong>Forfait</strong> : prix fixe par repas/créneau. <strong>Au temps</strong> : facturation
                  selon la durée de présence (entrée → sortie), au quart d'heure, à la demi-heure ou à l'heure.
                  Le tarif du service doit correspondre au mode choisi (onglet Tarifs).
                </Alert>
                <div className="grid gap-4 sm:grid-cols-3">
                  <Field label="Cantine" htmlFor="mode-cantine">
                    <select id="mode-cantine" value={params.modeFacturationCantine} onChange={(e) => patch('modeFacturationCantine', e.target.value as 'forfait' | 'temps')}
                      className="w-full rounded-r-md border border-border bg-surface px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cantine">
                      <option value="forfait">Forfait (au repas)</option>
                      <option value="temps">Au temps de présence</option>
                    </select>
                  </Field>
                  <Field label="Périscolaire" htmlFor="mode-perisco">
                    <select id="mode-perisco" value={params.modeFacturationPeriscolaire} onChange={(e) => patch('modeFacturationPeriscolaire', e.target.value as 'forfait' | 'temps')}
                      className="w-full rounded-r-md border border-border bg-surface px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cantine">
                      <option value="forfait">Forfait (au créneau)</option>
                      <option value="temps">Au temps de présence</option>
                    </select>
                  </Field>
                  <Field label="ALSH (centre de loisirs)" htmlFor="mode-alsh">
                    <select id="mode-alsh" value={params.modeFacturationAlsh} onChange={(e) => patch('modeFacturationAlsh', e.target.value as 'forfait' | 'temps')}
                      className="w-full rounded-r-md border border-border bg-surface px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cantine">
                      <option value="forfait">Forfait (journée / demi-journée)</option>
                      <option value="temps">Au temps de présence</option>
                    </select>
                  </Field>
                </div>

                {(params.modeFacturationCantine === 'temps' || params.modeFacturationPeriscolaire === 'temps' || params.modeFacturationAlsh === 'temps') && (
                  <div className="mt-4 grid gap-4 border-t border-border/60 pt-4 sm:grid-cols-2">
                    <Field label="Unité de temps facturée" htmlFor="unite">
                      <select id="unite" value={params.facturationUniteMinutes} onChange={(e) => patch('facturationUniteMinutes', Number(e.target.value))}
                        className="w-full rounded-r-md border border-border bg-surface px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cantine">
                        <option value={15}>Quart d'heure (15 min)</option>
                        <option value={30}>Demi-heure (30 min)</option>
                        <option value={60}>Heure pleine (60 min)</option>
                      </select>
                    </Field>
                    <Field label="Arrondi du temps" htmlFor="arrondi">
                      <select id="arrondi" value={params.facturationArrondi} onChange={(e) => patch('facturationArrondi', e.target.value as 'sup' | 'proche')}
                        className="w-full rounded-r-md border border-border bg-surface px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cantine">
                        <option value="sup">Au supérieur (toute unité commencée est due)</option>
                        <option value="proche">Au plus proche</option>
                      </select>
                    </Field>
                  </div>
                )}
              </CardBody>
            </Card>

            {/* Automatisation mensuelle de la facturation */}
            <Card>
              <CardBody>
                <h2 className="mb-1 font-display text-lg font-semibold text-ink">Automatisation de la facturation</h2>
                <p className="mb-3 text-sm text-ink-muted">
                  Émet automatiquement les factures chaque mois, à la date que vous choisissez. Si désactivé,
                  l'émission reste manuelle depuis l'écran Facturation.
                </p>

                <label className="flex items-center gap-3 rounded-r-lg border border-border bg-surface/60 p-3">
                  <input type="checkbox" checked={params.autoFacturationActive}
                    onChange={(e) => patch('autoFacturationActive', e.target.checked)}
                    className="h-4 w-4 accent-[oklch(var(--c-cantine))]" />
                  <span className="text-sm font-medium text-ink">Activer l'émission automatique mensuelle</span>
                </label>

                {params.autoFacturationActive && (
                  <div className="mt-4 space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <Field label="Jour du mois" htmlFor="auto-jour" hint="Entre 1 et 28.">
                        <select id="auto-jour" value={params.autoFacturationJour} onChange={(e) => patch('autoFacturationJour', Number(e.target.value))}
                          className="w-full rounded-r-md border border-border bg-surface px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cantine">
                          {Array.from({ length: 28 }, (_, i) => i + 1).map((j) => <option key={j} value={j}>{j}</option>)}
                        </select>
                      </Field>
                      <Field label="Heure d'envoi" htmlFor="auto-heure" hint="Heure française (le décalage serveur est géré automatiquement).">
                        <select id="auto-heure" value={params.autoFacturationHeure} onChange={(e) => patch('autoFacturationHeure', Number(e.target.value))}
                          className="w-full rounded-r-md border border-border bg-surface px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cantine">
                          {Array.from({ length: 24 }, (_, i) => i).map((h) => <option key={h} value={h}>{String(h).padStart(2, '0')}h00</option>)}
                        </select>
                      </Field>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <Field label="Période facturée" htmlFor="auto-periode">
                        <select id="auto-periode" value={params.autoFacturationPeriode} onChange={(e) => patch('autoFacturationPeriode', e.target.value as 'mois_ecoule' | 'mois_en_cours')}
                          className="w-full rounded-r-md border border-border bg-surface px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cantine">
                          <option value="mois_ecoule">Le mois écoulé (recommandé)</option>
                          <option value="mois_en_cours">Le mois en cours</option>
                        </select>
                      </Field>
                      <Field label="Type de facture" htmlFor="auto-groupee">
                        <select id="auto-groupee" value={params.autoFacturationGroupee ? 'oui' : 'non'} onChange={(e) => patch('autoFacturationGroupee', e.target.value === 'oui')}
                          className="w-full rounded-r-md border border-border bg-surface px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cantine">
                          <option value="oui">Une facture groupée multi-services</option>
                          <option value="non">Une facture séparée par service</option>
                        </select>
                      </Field>
                    </div>

                    <Field label="Services facturés automatiquement" htmlFor="auto-svc">
                      <div className="flex flex-wrap gap-3 pt-1">
                        {(['cantine', 'periscolaire', 'alsh'] as const).map((s) => (
                          <label key={s} className="inline-flex items-center gap-2 text-sm text-ink">
                            <input type="checkbox" checked={params.autoFacturationServices.includes(s)}
                              onChange={(e) => patch('autoFacturationServices', e.target.checked
                                ? [...new Set([...params.autoFacturationServices, s])]
                                : params.autoFacturationServices.filter((v) => v !== s))}
                              className="h-4 w-4 accent-[oklch(var(--c-cantine))]" />
                            {s === 'cantine' ? 'Cantine' : s === 'periscolaire' ? 'Périscolaire' : 'ALSH'}
                          </label>
                        ))}
                      </div>
                    </Field>

                    <label className="flex items-start gap-3 rounded-r-lg border border-border bg-surface/60 p-3">
                      <input type="checkbox" checked={params.autoFacturationEnvoiEmail}
                        onChange={(e) => patch('autoFacturationEnvoiEmail', e.target.checked)}
                        className="mt-0.5 h-4 w-4 accent-[oklch(var(--c-cantine))]" />
                      <span className="text-sm text-ink">
                        <span className="font-medium">Envoyer chaque facture par e-mail</span>
                        <span className="block text-xs text-ink-muted">Le PDF de la facture est envoyé automatiquement au responsable de la famille (s'il a une adresse e-mail), avec un lien vers son espace famille.</span>
                      </span>
                    </label>

                    {params.autoFacturationLastRun && (
                      <p className="text-xs text-ink-subtle">Dernière exécution automatique : {new Date(params.autoFacturationLastRun).toLocaleString('fr-FR')}</p>
                    )}

                    <div className="border-t border-border/60 pt-3">
                      <Button variant="secondary" size="sm" loading={autoRun.busy} disabled={dirty} onClick={() => void execAuto()}>
                        Émettre maintenant (test)
                      </Button>
                      {dirty && (
                        <Alert variant="warning" className="mt-2">
                          Enregistrez d'abord vos paramètres (bouton « Enregistrer » en bas) : le test utilise les réglages sauvegardés.
                        </Alert>
                      )}
                      {autoRun.msg && <Alert variant="success" className="mt-2">{autoRun.msg}</Alert>}
                      <p className="mt-2 text-2xs text-ink-subtle">
                        Émet immédiatement les factures de la période configurée, sans attendre la date automatique.
                        Idempotent : les factures déjà émises ne sont pas dupliquées. L'heure d'envoi est en heure française.
                      </p>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>

            {/* Facture : date limite, mention, en-tête mairie */}
            <Card>
              <CardBody>
                <div className="mb-3 flex items-center gap-2">
                  <Settings className="h-5 w-5 text-cantine-fg" aria-hidden="true" />
                  <h2 className="font-display text-base font-semibold text-ink">Facture &amp; en-tête</h2>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Délai de paiement (jours après émission)" htmlFor="delaiP">
                    <Input id="delaiP" type="number" min={1} max={120} value={params.delaiPaiementJours}
                      onChange={(e) => patch('delaiPaiementJours', Math.max(1, Math.min(120, Number(e.target.value) || 30)))} />
                  </Field>
                  <Field label="Mention en pied de facture (optionnel)" htmlFor="mention">
                    <Input id="mention" value={params.mentionPiedFacture ?? ''} placeholder="Ex. : merci de régler avant la date limite."
                      onChange={(e) => patch('mentionPiedFacture', e.target.value || null)} />
                  </Field>
                </div>

                {entete && (
                  <div className="mt-5 border-t border-border/60 pt-4">
                    <div className="mb-2 text-sm font-medium text-ink">En-tête de la commune sur les factures</div>
                    <div className="mb-3 flex flex-wrap gap-2">
                      {([
                        { v: 'auto', l: 'Composé automatiquement' },
                        { v: 'image', l: 'Image téléversée' },
                      ] as const).map((o) => (
                        <button key={o.v} type="button" onClick={() => void changerModeEntete(o.v)}
                          className={['rounded-r-md border px-3 py-1.5 text-sm font-medium transition-colors',
                            entete.mode === o.v ? 'border-cantine bg-cantine/10 text-cantine-fg' : 'border-border text-ink-muted hover:text-ink'].join(' ')}>
                          {o.l}
                        </button>
                      ))}
                    </div>

                    {entete.mode === 'auto' && (
                      <p className="text-2xs text-ink-subtle">L'en-tête est composé à partir du nom et de l'adresse de la commune. Aucune action requise.</p>
                    )}

                    {entete.mode === 'image' && (
                      <div className="flex flex-wrap items-center gap-3">
                        <label className="inline-flex cursor-pointer items-center gap-2 rounded-r-md border border-border px-3 py-2 text-sm text-ink hover:bg-cantine/5">
                          <Upload className="h-4 w-4" />
                          {uploading ? 'Téléversement…' : 'Téléverser une image (PNG/JPG)'}
                          <input type="file" accept="image/png,image/jpeg" className="hidden"
                            onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadEntete(f); e.target.value = ''; }} />
                        </label>
                        {entete.aUneImage && (
                          <span className="inline-flex items-center gap-1 text-2xs text-success-fg">
                            <ImageIcon className="h-3.5 w-3.5" /> Image en place
                          </span>
                        )}
                      </div>
                    )}

                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                      <Field label="Mention légale (pied de page)" htmlFor="mlegale">
                        <Input id="mlegale" value={entete.mentionLegale ?? ''} placeholder="Ex. : Régie cantine — Mairie de …"
                          onChange={(e) => setEntete({ ...entete, mentionLegale: e.target.value || null })} onBlur={() => void sauverEnteteTexte()} />
                      </Field>
                      <Field label="IBAN / RIB régie (optionnel)" htmlFor="iban">
                        <Input id="iban" value={entete.iban ?? ''} placeholder="FR76 …"
                          onChange={(e) => setEntete({ ...entete, iban: e.target.value || null })} onBlur={() => void sauverEnteteTexte()} />
                      </Field>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>

            <div className="flex justify-end">
              <Button variant="cantine" loading={saving} onClick={() => void enregistrer()}>
                <Save className="h-4 w-4" /> Enregistrer
              </Button>
            </div>
          </div>
        )}
      </main>
    </ScolaireLayout>
  );
}
